/**
 * The audience builder's rule options, from the backend's attribute catalog
 * (listAudienceAttributeCatalog). A rule whose attribute or operator the
 * catalog doesn't list — or any rule, while the catalog hasn't loaded — keeps
 * its own value as an option, labelled as itself: a select with no matching
 * option would show (and on the next edit, save) a different attribute.
 */
import type { AudienceAttributeCatalogEntry } from "../data/api-types";

export interface RuleOption {
  value: string;
  label: string;
}

const OPERATOR_LABELS: Record<string, string> = {
  eq: "is",
  neq: "is not",
  in: "is one of",
  not_in: "is not one of",
  contains: "contains",
  not_contains: "does not contain",
  gte: "is at least",
  lte: "is at most",
};

export function attributeOptions(
  catalog: AudienceAttributeCatalogEntry[],
  currentAttribute: string,
): RuleOption[] {
  const options = catalog.map((entry) => ({
    value: entry.path,
    label: entry.label,
  }));
  if (!catalog.some((entry) => entry.path === currentAttribute)) {
    options.unshift({ value: currentAttribute, label: currentAttribute });
  }
  return options;
}

export function operatorOptions(
  catalog: AudienceAttributeCatalogEntry[],
  attribute: string,
  currentOperator: string,
): RuleOption[] {
  const entry = catalog.find((e) => e.path === attribute);
  const operators = entry ? [...entry.operators] : [];
  if (!operators.includes(currentOperator)) operators.unshift(currentOperator);
  return operators.map((op) => ({
    value: op,
    label: OPERATOR_LABELS[op] ?? op,
  }));
}

/** The operator a rule takes when its attribute changes: its current one if
 * the new attribute allows it, else the new attribute's first. */
export function operatorForAttribute(
  catalog: AudienceAttributeCatalogEntry[],
  attribute: string,
  currentOperator: string,
): string {
  const entry = catalog.find((e) => e.path === attribute);
  if (!entry || entry.operators.includes(currentOperator)) {
    return currentOperator;
  }
  return entry.operators[0] ?? currentOperator;
}
